import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Filter, Layers, Blinds, X, SunDim, Moon, Home } from "lucide-react";
import { Star } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Sparkles } from "lucide-react";
import { useSelector } from "react-redux";
import { allProducts, roomTypeMappings } from "./data";

const ShopPage = () => {
	const [filters, setFilters] = useState({
		type: null,
		lightControl: null,
		roomType: null,
		priceRange: null, // Added for new filter
	});

	const country = useSelector((state) => state.country);

	// Comprehensive product catalog with enhanced metadata
	const products = useMemo(() => allProducts, []);

	// Memoized filter logic to prevent unnecessary re-renders
	const filteredProducts = useMemo(
		() =>
			products.filter(
				(product) =>
					(!filters.type || product.type === filters.type) &&
					(!filters.lightControl ||
						product.lightControl === filters.lightControl) &&
					(!filters.roomType || product.roomType.includes(filters.roomType)) &&
					(!filters.priceRange ||
						(product.price >= parseInt(filters.priceRange.split("-")[0]) &&
							product.price <= parseInt(filters.priceRange.split("-")[1])))
			),
		[filters, products]
	);

	// Filter options with icons and counts
	const filterOptions = {
		type: [
			{ value: "Roller", icon: Blinds },
			{ value: "Zebra", icon: Filter },
			{ value: "Sheer", icon: SunDim },
			{ value: "Simply Cellular", icon: Layers },
		],
		lightControl: [
			{ value: "Blackout", icon: Moon },
			{ value: "Privacy", icon: Home },
			{ value: "Transparency", icon: Layers },
			{ value: "Soft Filter", icon: SunDim },
			{ value: "Solar", icon: SunDim },
		],
		roomType: Object.values(roomTypeMappings)
			.map((roomType) => roomType.map((value) => ({ value })))
			.flat(),
		priceRange: [
			{ value: "0-300", label: "Under $300" },
			{ value: "300-600", label: "$300 - $600" },
			{ value: "600-1000", label: "$600 - $1,000" },
			{ value: "1000-5000", label: "$1,000 - $5,000" },
		],
	};

	// Calculate product counts for each filter option
	const getFilterCounts = (category, value) => {
		return products.filter(
			(product) =>
				(category === "type" ? product.type === value : true) &&
				(category === "lightControl" ? product.lightControl === value : true) &&
				(category === "roomType" ? product.roomType.includes(value) : true) &&
				(category === "priceRange"
					? product.price >= parseInt(value.split("-")[0]) &&
					  product.price <= parseInt(value.split("-")[1])
					: true)
		).length;
	};

	return (
		<div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>Premium Window Treatments | Off Cut Shades</title>
				<meta
					name="description"
					content="Discover meticulously crafted window solutions. Custom shades designed to elevate your space with unparalleled precision and style."
				/>
			</Helmet>

			{/* Compact Sale Banner */}
			{/* Hero Section */}
			<section className="relative pt-12 overflow-hidden">
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ type: "spring", stiffness: 400, damping: 30 }}
						className="text-center relative z-10"
					>
						<motion.span
							className="rounded-xl inline-block px-6 py-2 bg-primary text-white text-sm mb-8 font-light tracking-widest"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
						>
							SITEWIDE SALE: 50% OFF EVERYTHING
						</motion.span>
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Find Your Perfect
							<span className="relative ml-4">
								<span className="text-primary">Shade</span>
							</span>
						</h1>
						<p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
							Our precision-engineered window shades seamlessly blend
							technology, design, and functionality for your unique space.
						</p>
					</motion.div>
					{/* Decorative elements unchanged */}
				</div>
			</section>

			{/* Filters Section */}
			<section className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg">
				<div className="container mx-auto px-4 py-4">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<AnimatePresence mode="popLayout">
							{Object.entries(filterOptions).map(([category, options]) => (
								<motion.div
									key={category}
									layout
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									exit={{ opacity: 0, scale: 0.9 }}
									transition={{ type: "spring", stiffness: 400, damping: 30 }}
									className="relative"
								>
									<select
										value={filters[category] || ""}
										onChange={(e) =>
											setFilters((prev) => ({
												...prev,
												[category]: e.target.value || null,
											}))
										}
										className="
                      appearance-none
                      w-full
                      pl-10
                      pr-8
                      py-2.5
                      border-2
                      border-gray-200
                      rounded-xl
                      bg-white
                      text-gray-700
                      font-medium
                      focus:outline-none
                      focus:ring-2
                      focus:ring-primary/30
                      focus:border-primary
                      transition-all
                      duration-300
                      hover:border-primary/50
                    "
									>
										<option value="">
											{category
												.replace(/([a-z])([A-Z])/g, "$1 $2")
												.charAt(0)
												.toUpperCase() +
												category
													.replace(/([a-z])([A-Z])/g, "$1 $2")
													.slice(1)}{" "}
											({getFilterCounts(category, "")})
										</option>
										{options.map(({ value, label = value }) => (
											<option key={value} value={value}>
												{label} ({getFilterCounts(category, value)})
											</option>
										))}
									</select>
									<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
										{options.find((opt) => opt.value === filters[category])
											?.icon ? (
											React.createElement(
												options.find((opt) => opt.value === filters[category])
													?.icon,
												{ className: "w-5 h-5 text-primary" }
											)
										) : (
											<Filter className="w-5 h-5 text-gray-400" />
										)}
									</div>
								</motion.div>
							))}

							{/* Clear Filters */}
							{(filters.type ||
								filters.lightControl ||
								filters.roomType ||
								filters.priceRange) && (
								<motion.button
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									onClick={() =>
										setFilters({
											type: null,
											lightControl: null,
											roomType: null,
											priceRange: null,
										})
									}
									className="
                    flex
                    items-center
                    px-4
                    py-2.5
                    bg-gray-100
                    text-gray-700
                    rounded-xl
                    hover:bg-gray-200
                    transition-all
                    duration-300
                    font-medium
                    group
                  "
								>
									<X className="w-5 h-5 mr-2 group-hover:rotate-90 transition-transform" />
									Clear All
								</motion.button>
							)}
						</AnimatePresence>
					</div>
				</div>
			</section>

			{/* Featured Products Section */}
			<section className="py-12">
				<div className="container mx-auto px-4">
					<div className="grid md:grid-cols-3 gap-8">
						<AnimatePresence mode="popLayout">
							{filteredProducts
								.filter((product) => product.isFeatured)
								.slice(0, 3) // Show top 3 featured products
								.map((product) => (
									<ProductCard
										key={product.id}
										product={product}
										country={country}
									/>
								))}
						</AnimatePresence>
					</div>
				</div>
			</section>

			{/* Products Grid */}
			<section className="py-12">
				<div className="container mx-auto px-4">
					<div className="grid md:grid-cols-3 gap-8">
						<AnimatePresence mode="popLayout">
							{filteredProducts.map((product) => (
								<ProductCard
									key={product.id}
									product={product}
									country={country}
								/>
							))}
						</AnimatePresence>
					</div>

					{/* No Results State */}
					<AnimatePresence>
						{filteredProducts.length === 0 && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								className="text-center py-32"
							>
								<Sparkles className="w-16 h-16 text-primary/50 mx-auto mb-6" />
								<h3 className="text-4xl font-bold text-gray-900 mb-4">
									No Products Found
								</h3>
								<p className="text-xl text-gray-600 mb-8">
									Try adjusting your filters or explore our full collection
								</p>
								<motion.button
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
									onClick={() =>
										setFilters({
											type: null,
											lightControl: null,
											roomType: null,
											priceRange: null,
										})
									}
									className="inline-flex items-center px-8 py-4 bg-primary text-white rounded-xl font-medium shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 group"
								>
									Reset Filters
									<X className="w-5 h-5 ml-2 group-hover:rotate-90 transition-transform" />
								</motion.button>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</section>
			{/* Inspiration Gallery Section */}
			<section className="py-12 bg-gray-50">
				<div className="container mx-auto px-4">
					<h2 className="text-center text-3xl font-semibold mb-6">
						Inspiration Gallery
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{/* Placeholder for gallery images - to be populated from backend */}
						{[
							{
								id: 1,
								image: "/images/living.jpg",
								room: "Living Room",
							},
							{ id: 2, image: "/images/bedroom.jpg", room: "Bedroom" },
							{ id: 3, image: "/images/kitchen.jpg", room: "Kitchen" },
						].map((item) => (
							<div
								key={item.id}
								className="relative bg-white rounded-2xl overflow-hidden shadow-md"
							>
								<img
									src={item.image}
									alt={`Inspiration for ${item.room}`}
									className="w-full h-64 object-cover"
								/>
								<div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-4">
									{item.room}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Customer Testimonials Section */}
			<section className="py-12">
				<div className="container mx-auto px-4">
					<h2 className="text-center text-3xl font-semibold mb-6">
						What Our Customers Say
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{/* Placeholder for testimonials - to be populated from backend */}
						{[
							{
								id: 1,
								name: "S. Park",
								rating: 5,
								quote:
									"High-quality sunscreen shades that perfectly balance light control. They've transformed my living room with their modern look and functionality.",
							},
							{
								id: 2,
								name: "Mike W.",
								rating: 5,
								quote:
									"Exceeded our expectations with quality and modern design. The shades perfectly match our vision for the space.",
							},
							{
								id: 3,
								name: "Louise O.",
								rating: 5,
								quote:
									'Perfect fit and great quality at an affordable price. Loved the sample ordering process - made choosing so much easier. Fast delivery to BC, even for my 92" blind. Highly recommend!',
							},
						].map((testimonial) => (
							<motion.div
								key={testimonial.id}
								className="bg-white p-6 rounded-2xl shadow-md"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5 }}
							>
								<div className="flex items-center gap-2 mb-2">
									{[...Array(testimonial.rating)].map((_, i) => (
										<Star
											key={i}
											className="w-5 h-5 text-primary fill-current"
										/>
									))}
								</div>
								<p className="text-gray-600 italic mb-4">{testimonial.quote}</p>
								<p className="font-semibold text-gray-900">
									{testimonial.name}
								</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
};

const ProductCard = ({ product, country }) => {
	const originalPrice =
		country === "USA" ? product.price * 0.75 : product.price;
	const salePrice = originalPrice * 0.5;

	return (
		<motion.div
			key={product.id}
			layout
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, scale: 0.9 }}
			transition={{ type: "spring", stiffness: 400, damping: 30 }}
			className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500"
		>
			{/* Image Container */}
			<div className="relative aspect-[4/3] overflow-hidden">
				<motion.img
					src={product.image}
					alt={product.name}
					className="w-full h-full object-cover"
					whileHover={{ scale: 1.05 }}
					transition={{ duration: 0.6 }}
				/>
				<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

				{product.bestseller && (
					<div className="absolute top-4 left-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-primary px-3 py-1.5 rounded-full text-sm font-medium">
						<Star className="w-4 h-4 fill-primary" />
						Bestseller
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex flex-col p-6 space-y-4">
				<div className="flex justify-between items-start">
					<div>
						<h3 className="text-2xl font-semibold text-gray-900 mb-1">
							{product.name}
						</h3>
						<div className="flex items-center gap-4">
							<span className="text-gray-500 line-through text-lg">
								${originalPrice?.toFixed(2)} {country === "USA" ? "USD" : "CAD"}
							</span>
							<span className="text-primary font-semibold text-2xl">
								${salePrice?.toFixed(2)} {country === "USA" ? "USD" : "CAD"}
							</span>
						</div>
						{/* <div className="flex items-center gap-2 mt-2">
							{[...Array(5)].map((_, i) => (
								<Star
									key={i}
									className="w-5 h-5 text-yellow-400 fill-current"
								/>
							))}
							<span className="text-sm text-gray-500">(4.5/5)</span>
						</div> */}
					</div>
				</div>
				<div className="flex items-center gap-2 text-gray-700">
					<span className="font-normal text-gray-500">•</span>
					<span className="text-sm font-normal text-gray-500">
						{product.roomType.join(", ")}
					</span>
				</div>
				<div className="flex items-center gap-2 text-gray-700">
					<Layers className="w-5 h-5 text-primary" />
					{product.lightControl} Light Control
				</div>

				<div className="grid grid-cols-2 gap-3 mt-auto">
					<motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
						<Link
							to={`/customize/details?default=${product.type
								.toLowerCase()
								.replaceAll(" ", "_")}&fabric=${product.fabric.toLowerCase()}`}
							className="flex items-center justify-center w-full px-6 py-3 bg-primary text-white rounded-xl font-medium shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 group"
						>
							Customize
							<ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
						</Link>
					</motion.div>
					<motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
						<Link
							to={`/shop/info/${product.type
								.replaceAll(" ", "_")
								.toLowerCase()}&fabric=${product.fabric
								.toLowerCase()
								.replaceAll(" ", "_")}`}
							className="flex items-center justify-center w-full px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:border-primary/30 transition-all duration-300 group"
						>
							More Info
							<ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
						</Link>
					</motion.div>
				</div>
			</div>
		</motion.div>
	);
};

export default ShopPage;
